import { Divider, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { CopyableId } from '../CopyableText';

import { EntityRef } from '../EntityRef';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { inlineOrganizationForMember } from '../../lib/organizationLookup';
import type { Organization, OrganizationMember } from '../../types/api';
import { DetailField, DetailFieldGrid } from './DetailField';
import { MemberRolesSection } from './MemberRolesSection';

export function OrganizationMemberDetailPanel({
    member,
    orgById,
}: {
    member: OrganizationMember;
    orgById?: Map<string, Organization>;
}) {
    const { t } = useTranslation();
    const lookups = useAdminLookups();
    const user = lookups.resolveUser(member.userId, member.user);
    const org = lookups.resolveOrganization(
        member.organizationId,
        inlineOrganizationForMember(member, orgById ?? lookups.organizations),
    );
    const role = lookups.resolveRole(member.roleId, member.role);
    const empty = t('common.empty');

    return (
        <Stack gap="lg">
            <DetailFieldGrid>
                <DetailField
                    label={t('fields.user')}
                    value={
                        <EntityRef
                            title={user.title}
                            id={user.id}
                            secondary={user.secondary}
                            href={`/users?id=${member.userId}`}
                        />
                    }
                />
                <DetailField
                    label={t('fields.organization')}
                    value={
                        <EntityRef
                            title={org.title}
                            id={org.id}
                            href={`/organizations?id=${member.organizationId}`}
                            hideId
                        />
                    }
                />
                <DetailField label={t('fields.role')} value={<EntityRef title={role.title} id={role.id} />} />
                <DetailField label={t('fields.membershipId')} value={<CopyableId id={member.id} />} />
                <DetailField
                    label={t('fields.created')}
                    value={member.createdAt ? new Date(member.createdAt).toLocaleString() : empty}
                />
            </DetailFieldGrid>
            <Divider />
            <MemberRolesSection member={member} />
        </Stack>
    );
}
